import { defineComponent, onUnmounted, reactive, watch, inject, onMounted } from 'vue'
import styles from './styles.module.scss'
import { SearchListItemModel, SearchListModel } from './searchList.model'
import debounce from 'lodash.debounce'
import Tracker from '@nio-fe/nio-tracker-web'
import List from './list'
import { Field } from 'vant'
import { FilterItem, FilterKey, FilterState } from '../filter/filter.model'
import { useAppStore } from '../../stores/app'
import { useComponentStore } from '../../nsh5/localStores/component'
import { getSuggestHome } from '../../apis/map.api'
import { MapViewMode, PointType } from '../../contract/map'

export type HomeSearchInputProps = {
  modelValue?: string
  placeholder?: string
  className?: string
  type?: string
  disabled?: boolean
  autofocus?: boolean
  prefix?: () => JSX.Element
  onSearching?: () => void
  onSelect?: (
    poiResource: {
      name: string
      location: string
      pointType: string
      pointSubType: string
      id: string
      address: string
    },
    type?: string
  ) => void
  onFocus?: () => void
  onBlur?: () => void
  handleChange?: () => void
  onListRender?: (vnode: JSX.Element) => JSX.Element | null
  onSearchIconClick?: () => void
  onClear?: () => void
  onChange?: (keyword: string) => void
}
/**
 * 首页搜索
 */
export default defineComponent({
  name: 'home-search-input',
  props: [
    'className',
    'placeholder',
    'onSelect',
    'onFocus',
    'onSearchIconClick',
    'onBlur',
    'onClear',
    'modelValue',
    'onChange',
  ],
  setup: (props: HomeSearchInputProps, { slots }) => {
    const appStore = useAppStore()
    const componentStore = useComponentStore()
    const client = inject<string>('client', '')
    // const nioStoreTitle = (process.config as any).title.nioStore

    const state = reactive({
      keyword: '',
      loading: false,
      suggestsPois: [] as SearchListItemModel[], // 地点
      suggestsTestDrive: [] as SearchListItemModel[], // 试驾点
      suggestsNioStore: [] as SearchListItemModel[], // 蔚来门店
      suggestsServiceCenter: [] as SearchListItemModel[], // 服务中心
      suggestsRecharge: [] as SearchListItemModel[], // 加电资源
      suggestsRechargePS: [] as SearchListItemModel[], // 换电资源
      suggestsRechargeCS: [] as SearchListItemModel[], // 充电资源
    })

    let tempValue = ''
    let keepKeywords = false

    const clearSearchList = () => {
      state.suggestsPois = []
      state.suggestsTestDrive = []
      state.suggestsNioStore = []
      state.suggestsServiceCenter = []
      state.suggestsRecharge = []
      state.suggestsRechargePS = []
      state.suggestsRechargeCS = []
    }

    const fetch = debounce(
      () => {
        const tempValueTrim = tempValue.trim()
        if (tempValueTrim) {
          state.loading = true
          clearSearchList()
          void (async () => {
            const componentStore = useComponentStore()
            const typeFilter = componentStore.filter
              // .filter((item: FilterItem) => item.key !== FilterKey.Recharge) // 20240523 兼容处理  不展示加电资源   用于搜索列表不展示加电资源tab
              .filter((item: FilterItem) => item.filterState == FilterState.On)
              .map((item: FilterItem) => item.key) // 获取选中第一层过滤器
            typeFilter.push('poi') // 地点默认固定存在
            const res: any = await getSuggestHome(
              componentStore.myLocation?.code || '',
              tempValueTrim,
              typeFilter?.length ? typeFilter.join(',') : ''
            )
            if (res.data) {
              const searchResults = res.data.searchResults || new SearchListModel()
              state.suggestsPois = searchResults.suggestsPois
              state.suggestsTestDrive = searchResults.suggestsTestDrive
              state.suggestsNioStore = searchResults.suggestsNioStore
              state.suggestsServiceCenter = searchResults.suggestsServiceCenter
              state.suggestsRecharge = searchResults.suggestsRecharge
              state.suggestsRechargePS = searchResults.suggestsRechargePS
              state.suggestsRechargeCS = searchResults.suggestsRechargeCS
            }
            state.loading = false
          })()
        }
      },
      400,
      {}
    )

    watch(
      () => [props.modelValue],
      () => {
        state.keyword = props.modelValue || ''
      },
      { immediate: true }
    )

    const handleChange = (val: string) => {
      componentStore.$patch({ showSearchBackGround: true })
      state.keyword = val
      tempValue = val
      if (val === '') {
        componentStore.$patch({ showSearchBackGround: false })
        state.loading = false
        clearSearchList()
        // componentStore.triggerPanel()
        componentStore.$patch({
          searchListPanel: false,
        })
      }
      fetch.cancel()
      if (val) {
        // 若筛选看板打开则关闭
        componentStore.filterPanel && componentStore.bus.emit('closeFilterPanel')
        // 若详情看板打开则关闭
        componentStore.detailPanel && componentStore.bus.emit('closeDetailPanel')
        componentStore.triggerPanel('searchListPanel')
        fetch()
      }
      props?.onChange?.(state.keyword)
      componentStore.$patch({
        searchKeywordInMobile: state.keyword, // mobile 端保存搜索关键词
      })
    }

    // 点击后选项
    const handleSelect = (index: number, itemType: string) => {
      let evenObj = {} as SearchListItemModel
      let searchtypes = ''
      switch (itemType) {
        case PointType.Poi:
          evenObj = state.suggestsPois?.[index]
          searchtypes = '地点'
          break
        case PointType.TestDrive:
          evenObj = state.suggestsTestDrive?.[index]
          searchtypes = componentStore.filterNameMap.test_drive
          break
        case PointType.NioStore:
          evenObj = state.suggestsNioStore?.[index]
          searchtypes = componentStore.filterNameMap.nio_store
          break
        case PointType.ServiceCenter:
          evenObj = state.suggestsServiceCenter?.[index]
          searchtypes = componentStore.filterNameMap.service_center
          break
        // case PointType.Recharge:
        //   evenObj = state.suggestsRecharge?.[index]
        //   searchtypes = '加电资源'
        //   break
        case PointType.RechargePS:
          evenObj = state.suggestsRechargePS?.[index]
          searchtypes = componentStore.filterNameMap['recharge|ps']
          break
        case PointType.RechargeCS:
          evenObj = state.suggestsRechargeCS?.[index]
          searchtypes = componentStore.filterNameMap['recharge|cs']
          break

        default:
          break
      }
      // evenObj.pointType === 'poi' 为地点  202311221迭代前 地点没有类型
      props?.onSelect?.(evenObj)
      Tracker.track(
        'nsh5_homesearchpagelistclick',
        Object.assign(
          {},
          {
            channel: window.channel,
            client: window.client,
            brand: (process.config as any).brand,
            screen_orientation: window.screen_orientation,
            view_mode: componentStore.viewMode,
            container: window.container,
            user_identity: window.user_identity,
            trigger: 'click',
            element: '点击搜索项',
            page_source: window.page_source,
          },
          {
            keywords: state.keyword,
            searchtypes,
            poiname: evenObj.name,
            resourcesid: itemType != 'poi' ? evenObj.id : null,
            poiaddress: itemType == 'poi' ? evenObj.address : null,
          }
        )
      )
      // state.keyword = `${evenObj.name}` // 20240912 点击候选项不清空输入框
      clearSearchList()
      componentStore.triggerPanel()
    }

    const handleTabClick = () => {
      // 搜索列表里面tab切换，不清空
      keepKeywords = true
    }

    const onBlur = () => {
      // ******* 20240718 删除 影响fellow用户的复制 ********
      // state.keyword.length > 0 || props?.onBlur?.()
      // setTimeout(() => {
      //   // 搜索下直接点击筛选勾选，会先触发onBlur再触发filterChanged，所以这里加个延迟
      //   if (!keepKeywords) {
      //     if (componentStore.tempSearchPoiResource?.name) {
      //       state.keyword = componentStore.tempSearchPoiResource.name
      //       clearSearchList()
      //     }
      //   }
      //   keepKeywords = false
      // }, 500)

      // 20240912 键盘清空后 blur 清空缓存的选中点
      if (state.keyword.length > 0) {
        return
      } else {
        handleClear()
      }
    }

    // componentStore.bus.on('handleHomeSearchClear', () => {
    //   handleClear()
    // })
    const handleClear = () => {
      handleChange('') // 清除关键词
      componentStore.$patch({
        tempSearchPoiResource: null,
      })
      componentStore.bus.emit('forceClearBackList') // 清空后退列表
      props?.onClear?.()
    }

    // const searchIconClick = () => {
    //   props?.onSearchIconClick?.()
    // }

    const onFocus = () => {
      props?.onFocus?.()
      keepKeywords = false
      handleChange(state.keyword)
    }

    const closeSearchListPanel = () => {
      // state.keyword = '' // 20240912 关闭搜索列表不清空输入框
      clearSearchList()
      fetch.cancel()
      // onBlur()
    }

    componentStore.bus.on('closeSearchListPanel', closeSearchListPanel)

    const searchListFilterChanged = () => {
      keepKeywords = true
      const searchPoiResourceName = componentStore.tempSearchPoiResource?.name || ''
      if (searchPoiResourceName && state.keyword != searchPoiResourceName) {
        // 选了点且输入keyword变化
        handleChange(state.keyword) // 出现搜索候选
      } else if (searchPoiResourceName && state.keyword == searchPoiResourceName) {
        // 选了点且输入keyword无变化
        return
      } else {
        // 没有选点
        handleChange(state.keyword) // 出现搜索候选
      }
    }
    componentStore.bus.on('filterChanged', searchListFilterChanged)

    // 退出搜索态
    const searchPanelBack = () => {
      componentStore.$patch({
        showSearchPanel: false,
        showHandleBox: true,
      })
    }

    // 20230524 - 根据值显示搜索背景
    watch(
      () => state.keyword,
      (val: string | null) => {
        if (val === '') {
          componentStore.$patch({ showSearchBackGround: false })
        } else {
          componentStore.$patch({ showSearchBackGround: true })
        }
      }
    )

    onMounted(() => {
      componentStore.bus.on('handleHomeSearchClear', () => {
        handleClear()
      })
    })

    onUnmounted(() => {
      // ! 一定要加上off，因为组件会不停的注销和挂载，如果不off，会导致多次注册事件
      componentStore.bus.off('handleHomeSearchClear')
      componentStore.bus.off('closeSearchListPanel')
      componentStore.bus.off('filterChanged', searchListFilterChanged)
    })

    return () => (
      // 首页搜索
      <>
        <div
          class={[styles.search, componentStore.showSearchPanel ? styles.searchPanel : '']
            .concat(props.className ? [props.className] : [])
            .join(' ')}
        >
          {client === 'mobile' && componentStore.showSearchPanel ? (
            <ns-icon
              name={(process.config as any).icon.back}
              color="#050B27"
              size="23"
              class={styles.searchButton}
              onClick={searchPanelBack}
            />
          ) : (
            <ns-icon
              name={(process.config as any).icon.search_mid_outline}
              color="#999DAE"
              size="23"
              class={styles.searchButton}
            />
          )}
          <Field
            class={styles.input}
            autocomplete="off"
            border={false}
            disabled={props.disabled}
            modelValue={state.keyword}
            autofocus={props.autofocus}
            placeholder={props.placeholder}
            onUpdate:modelValue={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            clearTrigger="always"
            rightIcon="right"
            onClickRightIcon={handleClear}
            iconPrefix="van-icon van-icon-clear van-field__clear"
            v-slots={{
              button: () =>
                props.prefix ? (
                  <span
                    class={[styles.icon]
                      .concat(props.type === 'orig' ? [styles.color] : [])
                      .join(' ')}
                  ></span>
                ) : null,
              'right-icon': () => (
                <i class="van-badge__wrapper van-icon van-icon-clear van-field__clear"></i>
              ),
            }}
          />
        </div>
        {slots.searchInput && slots.searchInput()}
        {
          <div class={styles.listContainer}>
            <List
              dataSourcePois={state.suggestsPois}
              dataSourceTestDrive={state.suggestsTestDrive}
              dataSourceNioStore={state.suggestsNioStore}
              dataSourceServiceCenter={state.suggestsServiceCenter}
              dataSourceRecharge={state.suggestsRecharge}
              dataSourceRechargePS={state.suggestsRechargePS}
              dataSourceRechargeCS={state.suggestsRechargeCS}
              keyword={state.keyword}
              loading={state.loading}
              onItemClick={handleSelect}
              onTabClick={handleTabClick}
            ></List>
          </div>
        }
      </>
    )
  },
})
